import ComponentMetricsBoundary from '@/components/ComponentMetricsBoundary/ComponentMetricsBoundary'
import AbandonedSelection from '@/components/TripActivity/AbandonedSelection'
import useBootstrapData from '@/hooks/useBootstrapData'
import useClientSideSignedInData from '@/hooks/useClientSideSignedInData'
import useSeti from '@/hooks/useSeti'
import { Button, Heading } from '@pcln/horizon'
import { RecentSelection } from '@pcln/horizon-customer-growth-components'
import { Box, Text, theme } from 'pcln-design-system'
import { useEffect, type Dispatch, type SetStateAction } from 'react'
import { useMediaQuery } from 'react-responsive'
import ConditionalRender, { Experiment, Original } from '../ConditionalRender'
import useTripActivityData from './hooks/useTripActivityData'
import RecentSearchAndPriceWatchCarousel from './RecentSearchAndPriceWatchCarousel'
import RecentSearchCarousel from './RecentSearchCarousel'
import { recentSelectionMapper } from './recentSelectionMapper'
import { tripActivityVariants } from './TripActivity.variant'
import type {
  AbandonedSelection as AbandonedSelectionType,
  RecentSearchAndPriceWatchList,
  RecentTripSearches,
  SearchProductList,
} from './types'
import { onViewAllButtonClick } from './utils'

function isRecentTripSearches(
  _item: RecentSearchAndPriceWatchList,
  isPriceWatchVariant: boolean,
): _item is ReadonlyArray<RecentTripSearches> {
  return isPriceWatchVariant === false
}
export default function TripActivity({
  productType,
  setIsVisible,
  shouldUseSmallerHeading,
  abandonedSelectionData,
  hideAbandonedSelection,
  pageName,
}: {
  setIsVisible?: Dispatch<SetStateAction<boolean>>
  productType: SearchProductList
  shouldUseSmallerHeading?: boolean
  abandonedSelectionData?: AbandonedSelectionType | null
  hideAbandonedSelection?: boolean
  pageName: 'homepage' | 'landing'
}) {
  const {
    signInStatus: { signedIn: serverSignedIn },
  } = useBootstrapData()
  const { signedIn: clientSideSignedIn } = useClientSideSignedInData()
  const signedIn = serverSignedIn || clientSideSignedIn

  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme.breakpoints.lg as string})`,
  })
  const isShowPriceWatch =
    useSeti({ experimentCode: 'HP_PUWYLO_PRICEWATCH', shouldFireImpression: false }) === 'VARIANT'
  const { atMostFiveRecentSearchCards, abandonedSelection } = useTripActivityData(
    productType,
    abandonedSelectionData,
  )

  const showAbandonedSelection = abandonedSelection && !hideAbandonedSelection
  const showRecentSelections =
    useSeti({
      experimentCode: 'WEB_HP_HORIZON_SELECTION',
      shouldFireImpression: Boolean(showAbandonedSelection),
    }) === 'VARIANT'

  const hasRecentSearchCards = atMostFiveRecentSearchCards.length > 0
  useEffect(() => {
    if (setIsVisible) {
      setIsVisible(hasRecentSearchCards || abandonedSelection !== null)
    }
  }, [hasRecentSearchCards, abandonedSelection, setIsVisible, hideAbandonedSelection])

  const { heading } = tripActivityVariants({
    useSmallerHeading: shouldUseSmallerHeading,
  })
  return hasRecentSearchCards || showAbandonedSelection ? (
    <ComponentMetricsBoundary componentId='TripActivity'>
      <div className='relative'>
        <div className='flex items-start justify-between'>
          <Heading as='h2' palette='neutral' shade='13' className={heading()}>
            Pick up where you left off
          </Heading>
          {isDesktop && (
            <Button
              type='secondary'
              size='sm'
              data-testid='view-all-button-top-right'
              onClick={() => onViewAllButtonClick(pageName)}
            >
              View All Recent Activity
            </Button>
          )}
        </div>
        {showAbandonedSelection && (
          <>
            <Box mb={2}>
              {showRecentSelections ? (
                <RecentSelection {...recentSelectionMapper(abandonedSelection)} />
              ) : (
                <AbandonedSelection abandonedSelection={abandonedSelection} pageName={pageName} />
              )}
            </Box>
            {abandonedSelection.productType === 'hotels' && (
              <Text textStyle='caption' pt={2} pl={[0, null, 2]}>
                *Availability and pricing are subject to change
              </Text>
            )}
            {hasRecentSearchCards && (
              <Heading
                as='h3'
                palette='neutral'
                shade='13'
                className='pt-4 pb-2 text-heading4 md:pb-4 md:text-heading3'
              >
                Your recent searches
              </Heading>
            )}
          </>
        )}
        {hasRecentSearchCards && (
          <ConditionalRender
            experimentCode='HP_PUWYLO_PRICEWATCH'
            triggerExperiment={() => {
              return signedIn
            }}
          >
            <Original>
              <RecentSearchCarousel
                items={
                  isRecentTripSearches(atMostFiveRecentSearchCards, isShowPriceWatch)
                    ? atMostFiveRecentSearchCards
                    : []
                }
                pageName={pageName}
              />
            </Original>
            <Experiment>
              <RecentSearchAndPriceWatchCarousel
                items={
                  !isRecentTripSearches(atMostFiveRecentSearchCards, isShowPriceWatch)
                    ? atMostFiveRecentSearchCards
                    : []
                }
                pageName={pageName}
              />
            </Experiment>
          </ConditionalRender>
        )}

        {!isDesktop && (
          <div className='pt-6'>
            <Button
              type='secondary'
              size='sm'
              className='w-full'
              data-testid='view-all-button-bottom'
              onClick={() => onViewAllButtonClick(pageName)}
            >
              View All Recent Activity
            </Button>
          </div>
        )}
      </div>
    </ComponentMetricsBoundary>
  ) : null
}
